import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, House, Calendar, Plus, ArrowRight, CheckCircle, ClipboardText, Broadcast, Wrench, FilePdf, PaperPlaneTilt, StackSimple, X } from "@phosphor-icons/react";

const APPLIANCE_SEED_TYPES = [
  "Refrigerator", "Range / Stove", "Oven", "Microwave", "Dishwasher",
  "Washer", "Dryer", "Water Heater", "Furnace", "AC Condenser",
  "HVAC Air Handler", "Garbage Disposal",
];

// When user toggles an area ON, auto-suggest the appliances that live in it.
const AREA_TO_APPLIANCES = {
  has_kitchen: ["Refrigerator", "Range / Stove", "Dishwasher", "Microwave", "Garbage Disposal"],
  has_basement: ["Water Heater", "Furnace", "AC Condenser", "HVAC Air Handler"],
  has_laundry: ["Washer", "Dryer"],
  has_utility: ["Water Heater", "Furnace"],
};

const AREA_OPTIONS = [
  ["has_kitchen", "Kitchen"], ["has_living", "Living"], ["has_dining", "Dining"],
  ["has_hallway", "Hallway"], ["has_stairs", "Stairs"], ["has_basement", "Basement"],
  ["has_garage", "Garage"], ["has_yard", "Yard"], ["has_deck", "Deck"],
  ["has_porch", "Porch"], ["has_laundry", "Laundry Room"], ["has_utility", "Utility / Mudroom"],
  ["has_office", "Office"], ["has_foyer", "Foyer / Entry"], ["has_attic", "Attic"],
  ["has_pantry", "Pantry"], ["has_closet", "Closet"],
];

const TYPES = [
  { key: "move_out", label: "Move-Out", pillClass: "bg-red-600", desc: "Tenant leaving — document existing condition & damages" },
  { key: "move_in", label: "Move-In", pillClass: "bg-emerald-600", desc: "New tenant arriving — verify unit is turn-ready" },
  { key: "periodic", label: "Periodic", pillClass: "bg-sky-600", desc: "Mid-lease check-in — proactive maintenance walk" },
];

function ReportActions({ job }) {
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  const [briefing, setBriefing] = useState({ text: "", source: "", generated_at: "", edited_at: "", edited_by: "" });
  const [draft, setDraft] = useState("");
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [showTplSave, setShowTplSave] = useState(false);
  const [hidePrice, setHidePrice] = useState(!!job.hide_turn_price);
  const [priceBusy, setPriceBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  // Move-out / move-in / periodic sweeps → use the rich turn-style renderer with cover sheet
  // and room-by-room evidence photos. Fall back to the generic customer PDF for anything else.
  const isSweep = ["move_out","move_in","periodic"].includes(String(job.inspection_type || "").toLowerCase()) || job.is_turn;
  const downloadUrl = isSweep
    ? `${API}/jobs/${job.id}/turn-report-pdf?auth=${encodeURIComponent(token)}`
    : `${API}/jobs/${job.id}/pdf?customer=true&auth=${encodeURIComponent(token)}`;

  const openBriefing = async () => {
    setShowBriefing(true);
    setBriefingBusy(true);
    try {
      const [b, t] = await Promise.all([
        api.get(`/jobs/${job.id}/owner-briefing`),
        api.get(`/briefing-templates`),
      ]);
      setBriefing(b.data);
      setDraft(b.data.text || "");
      setTemplates(t.data?.templates || []);
      // If empty, kick off first-gen automatically by hitting the PDF endpoint or regen.
      if (!b.data.text) {
        await regenBriefing(true);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load briefing");
    } finally { setBriefingBusy(false); }
  };

  const loadTemplate = (tpl) => {
    setDraft(tpl.text || "");
    toast.success(`Loaded "${tpl.name}" template — remember to Save to lock it in`);
  };

  const saveAsTemplate = async () => {
    const name = tplName.trim();
    const text = draft.trim();
    if (!name) { toast.error("Give the template a name"); return; }
    if (!text) { toast.error("Briefing can't be empty"); return; }
    if (templates.length >= 20) { toast.error("You've hit the 20-template limit — delete one first"); return; }
    setBriefingBusy(true);
    try {
      const r = await api.post(`/briefing-templates`, { name, text });
      setTemplates((prev) => [r.data, ...prev]);
      setTplName("");
      setShowTplSave(false);
      toast.success(`Template "${name}" saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save template");
    } finally { setBriefingBusy(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/briefing-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter(x => x.id !== tpl.id));
      toast.success(`Template "${tpl.name}" deleted`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const togglePrice = async (next) => {
    setPriceBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/pdf-prefs`, { hide_turn_price: next });
      setHidePrice(next);
      toast.success(next ? "Turn Price hidden on report" : "Turn Price shown on report");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally { setPriceBusy(false); }
  };

  const saveBriefing = async () => {
    const text = draft.trim();
    if (!text) { toast.error("Briefing can't be empty"); return; }
    setBriefingBusy(true);
    try {
      const r = await api.patch(`/jobs/${job.id}/owner-briefing`, { text });
      setBriefing((b) => ({ ...b, ...r.data, text: r.data.text }));
      toast.success("Owner briefing saved — this is what the landlord will see");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBriefingBusy(false); }
  };

  const regenBriefing = async (silent = false) => {
    setRegenBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/owner-briefing/regen`);
      setBriefing((b) => ({ ...b, text: r.data.text, source: r.data.source, generated_at: r.data.generated_at, edited_at: "", edited_by: "" }));
      setDraft(r.data.text || "");
      if (!silent) toast.success(r.data.source === "ai" ? "Fresh AI briefing generated" : "Briefing regenerated (template fallback)");
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not regenerate");
    } finally { setRegenBusy(false); }
  };

  const sendEmail = async (e) => {
    e?.preventDefault?.();
    const to = email.trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/email-landlord-pdf`, { to_email: to, note: note.trim() });
      toast.success(`Report emailed to ${r.data.to}`);
      setShowEmail(false);
      setEmail("");
      setNote("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t-2 border-dashed border-slate-200 flex flex-wrap gap-2" data-testid={`insp-actions-${job.id}`}>
      {isSweep && (
        <button onClick={openBriefing} data-testid={`insp-briefing-open-${job.id}`}
          className="tactile-btn h-10 px-3 bg-amber-500 hover:bg-amber-600 text-white border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
          <ClipboardText weight="fill" size={14}/> Preview & Edit Briefing
        </button>
      )}
      <a href={downloadUrl} target="_blank" rel="noreferrer"
        data-testid={`insp-download-pdf-${job.id}`}
        className="tactile-btn h-10 px-3 bg-white border-2 border-[#0f172a] text-[#0f172a] hover:bg-slate-50 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
        <FilePdf weight="fill" size={14}/> Download Report
      </a>
      <button onClick={() => setShowEmail(true)}
        data-testid={`insp-email-open-${job.id}`}
        className="tactile-btn h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
        <PaperPlaneTilt weight="fill" size={14}/> Email Report
      </button>
      {(job.landlord_pdf_sends || []).length > 0 && (
        <span className="ml-auto self-center text-[10px] font-black uppercase tracking-widest text-slate-500"
          title={(job.landlord_pdf_sends || []).map(s => `${s.to} · ${new Date(s.at).toLocaleString()}`).join("\n")}>
          Sent {(job.landlord_pdf_sends || []).length}×
        </span>
      )}
      {showBriefing && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid={`insp-briefing-modal-${job.id}`} onClick={() => !briefingBusy && !regenBusy && setShowBriefing(false)}>
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[90vh] bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#f59e0b] flex flex-col">
            <div className="p-3 bg-amber-500 text-[#0f172a] flex items-center justify-between border-b-2 border-[#0f172a]">
              <div className="font-display font-black uppercase text-lg tracking-tight">📝 Owner Briefing</div>
              <button onClick={() => !briefingBusy && !regenBusy && setShowBriefing(false)} className="hover:opacity-70 font-black" data-testid={`insp-briefing-close-${job.id}`}>✕</button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-3">
              <div className="text-xs text-slate-600">
                This is the 2-paragraph note that appears at the top of the landlord's report for
                <b className="text-slate-900"> {job.property_address}</b>{job.unit ? ` · Unit ${job.unit}` : ""}. Edit it freely — your version is what the landlord sees.
              </div>
              {templates.length > 0 && (
                <div className="border-2 border-dashed border-slate-200 p-2" data-testid={`insp-briefing-templates-${job.id}`}>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">📎 Load a saved template</div>
                  <div className="flex flex-wrap gap-1.5">
                    {templates.map(tpl => (
                      <div key={tpl.id} className="inline-flex items-stretch border-2 border-slate-300 hover:border-fuchsia-400 group">
                        <button onClick={() => loadTemplate(tpl)} disabled={briefingBusy || regenBusy}
                          data-testid={`insp-briefing-tpl-${tpl.id}`}
                          className="px-2 py-1 text-[11px] font-black uppercase tracking-widest text-slate-700 hover:bg-fuchsia-50 disabled:opacity-40">
                          {tpl.name}
                        </button>
                        <button onClick={() => deleteTemplate(tpl)} title="Delete template"
                          data-testid={`insp-briefing-tpl-del-${tpl.id}`}
                          className="px-1.5 border-l-2 border-slate-300 text-[10px] font-black text-red-500 hover:bg-red-50 hover:text-red-700 opacity-40 group-hover:opacity-100">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap text-[10px] font-black uppercase tracking-widest">
                {briefing.source === "ai" && <span className="px-2 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300">✨ AI Generated</span>}
                {briefing.source === "edited" && <span className="px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300">✏️ Edited by {briefing.edited_by || "admin"}</span>}
                {briefing.source === "template" && <span className="px-2 py-1 bg-slate-100 text-slate-700 border border-slate-300">📄 Template</span>}
                {briefing.generated_at && <span className="text-slate-400 normal-case font-normal tracking-normal">· generated {new Date(briefing.generated_at).toLocaleString()}</span>}
                {briefing.edited_at && <span className="text-slate-400 normal-case font-normal tracking-normal">· edited {new Date(briefing.edited_at).toLocaleString()}</span>}
              </div>
              <textarea rows={12} value={draft} onChange={e => setDraft(e.target.value)}
                disabled={briefingBusy || regenBusy}
                placeholder={briefingBusy || regenBusy ? "Generating…" : "Two-paragraph note to the landlord…"}
                data-testid={`insp-briefing-textarea-${job.id}`}
                className="w-full px-3 py-2 border-2 border-slate-300 focus:border-amber-500 text-sm resize-y font-mono leading-relaxed"/>
              <div className="text-[10px] text-slate-400 font-black uppercase tracking-widest text-right">
                {draft.trim().length} chars · {draft.trim().split(/\s+/).filter(Boolean).length} words
              </div>
              <label className="flex items-center gap-2 cursor-pointer p-2 bg-slate-50 border-2 border-slate-200 hover:border-slate-300"
                data-testid={`insp-briefing-hide-price-${job.id}`}>
                <input type="checkbox" checked={hidePrice} onChange={e => togglePrice(e.target.checked)}
                  disabled={priceBusy} className="h-4 w-4 accent-fuchsia-600"/>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                  Hide Turn Price on this report
                </span>
                {priceBusy && <span className="text-fuchsia-500 text-[9px] animate-pulse ml-auto">saving…</span>}
              </label>
              {showTplSave && (
                <div className="border-2 border-fuchsia-400 bg-fuchsia-50 p-2 space-y-1.5">
                  <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">
                    Save as reusable template · {templates.length}/20 slots
                  </div>
                  <div className="flex gap-1.5">
                    <input type="text" value={tplName} onChange={e => setTplName(e.target.value)}
                      placeholder='e.g. "Solid Turn", "Damage Heavy"'
                      data-testid={`insp-briefing-tpl-name-${job.id}`} maxLength={80}
                      className="flex-1 h-9 px-2 border-2 border-fuchsia-300 text-sm bg-white"/>
                    <button onClick={saveAsTemplate} disabled={briefingBusy || !tplName.trim() || !draft.trim()}
                      data-testid={`insp-briefing-tpl-create-${job.id}`}
                      className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                      Save Template
                    </button>
                    <button onClick={() => { setShowTplSave(false); setTplName(""); }}
                      className="h-9 px-2 border-2 border-fuchsia-300 text-fuchsia-700 font-black uppercase text-[10px] tracking-widest">✕</button>
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-1 flex-wrap">
                <button onClick={() => regenBriefing(false)} disabled={briefingBusy || regenBusy}
                  data-testid={`insp-briefing-regen-${job.id}`}
                  className="h-11 px-3 border-2 border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50 font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-1.5">
                  {regenBusy ? "Generating…" : "🔄 Regenerate AI"}
                </button>
                <button onClick={() => setShowTplSave(true)} disabled={briefingBusy || regenBusy || !draft.trim() || showTplSave}
                  data-testid={`insp-briefing-tpl-open-${job.id}`}
                  className="h-11 px-3 border-2 border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50 font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-1.5">
                  💾 Save as Template
                </button>
                <button onClick={() => setDraft(briefing.text || "")} disabled={briefingBusy || regenBusy || draft === briefing.text}
                  data-testid={`insp-briefing-revert-${job.id}`}
                  className="h-11 px-3 border-2 border-slate-300 text-slate-600 hover:bg-slate-50 font-black uppercase text-xs tracking-widest disabled:opacity-40">
                  Revert
                </button>
                <div className="flex-1"/>
                <button onClick={() => setShowBriefing(false)} disabled={briefingBusy || regenBusy}
                  className="h-11 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest">
                  Close
                </button>
                <button onClick={saveBriefing} disabled={briefingBusy || regenBusy || !draft.trim() || draft === briefing.text}
                  data-testid={`insp-briefing-save-${job.id}`}
                  className="h-11 px-5 bg-amber-500 hover:bg-amber-600 text-[#0f172a] border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest disabled:opacity-40">
                  {briefingBusy ? "Saving…" : "Save Briefing"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEmail && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid={`insp-email-modal-${job.id}`} onClick={() => !busy && setShowEmail(false)}>
          <form onSubmit={sendEmail} onClick={e => e.stopPropagation()}
            className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#10b981]">
            <div className="p-3 bg-emerald-600 text-white flex items-center justify-between">
              <div className="font-display font-black uppercase text-lg tracking-tight">📧 Email Sweep Report</div>
              <button type="button" onClick={() => !busy && setShowEmail(false)} className="text-white/80 hover:text-white font-black" data-testid={`insp-email-close-${job.id}`}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-slate-600">
                Sends a signed link to the customer-safe PDF for <b className="text-slate-900">{job.title}</b>.
                No login required for the recipient.
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Recipient email *</label>
                <input type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="landlord@example.com"
                  data-testid={`insp-email-input-${job.id}`}
                  className="mt-1 w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Note (optional)</label>
                <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Sweep complete — see attached. Let me know any questions."
                  data-testid={`insp-email-note-${job.id}`}
                  className="mt-1 w-full px-3 py-2 border-2 border-slate-300 text-sm resize-none"/>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowEmail(false)} disabled={busy}
                  className="flex-1 h-11 border-2 border-slate-300 font-black uppercase text-xs tracking-widest">
                  Cancel
                </button>
                <button type="submit" disabled={busy}
                  data-testid={`insp-email-send-${job.id}`}
                  className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
                  {busy ? "Sending…" : "Send Report"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Small "wrong walk type?" fixer — swap move_out ↔ move_in ↔ periodic on an
// inspection you already started. Migrates any room work you did on the wrong
// phase into the correct one so you don't lose your notes/photos.
function ConvertTypeMenu({ job, onConverted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cur = String(job.inspection_type || "").toLowerCase();
  if (!cur || job.status === "completed" || job.is_turn || job.promoted_to_turn_id) return null;
  const options = [
    { key: "move_in",  label: "Move-In" },
    { key: "move_out", label: "Move-Out" },
    { key: "periodic", label: "Periodic" },
  ].filter(o => o.key !== cur);

  async function convert(to) {
    const dest = options.find(o => o.key === to)?.label;
    const confirmMsg = `Change this walk from ${cur.replace("_","-")} to ${dest}?\n\n` +
      `Any rooms you've already inspected will be moved to the ${dest} phase — no data loss.\n` +
      `The wrong phase will be blanked out.`;
    if (!window.confirm(confirmMsg)) { setOpen(false); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/convert-inspection-type`, { to_type: to });
      toast.success(
        r.data.migrated_rooms
          ? `Converted → ${dest}. Migrated ${r.data.migrated_rooms} room${r.data.migrated_rooms === 1 ? "" : "s"} of work.`
          : `Converted → ${dest}.`
      );
      setOpen(false);
      if (onConverted) await onConverted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Convert failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 flex-wrap" data-testid={`insp-convert-menu-${job.id}`}>
      <span className="text-[10px] uppercase font-black tracking-widest text-slate-400">Wrong walk type?</span>
      {options.map(o => (
        <button key={o.key} disabled={busy} onClick={() => convert(o.key)}
          className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-slate-100 hover:bg-amber-100 hover:text-amber-800 text-slate-600 disabled:opacity-40"
          data-testid={`insp-convert-${job.id}-${o.key}`}>
          Convert to {o.label}
        </button>
      ))}
    </div>
  );
}



function StatusPill({ status }) {
  const map = {
    assigned: "bg-amber-500 text-white",
    in_progress: "bg-sky-500 text-white",
    completed: "bg-emerald-600 text-white",
  };
  return <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${map[status] || "bg-slate-500 text-white"}`}>{status.replace("_", " ")}</span>;
}

export default function InspectionsBoard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("move_out");
  const [showCreate, setShowCreate] = useState(false);

  // Sidebar "+ New Move-Out" shortcut → `/admin/inspections?new=move_out`
  // auto-opens the create modal with the type pre-selected so it's a
  // single-tap flow from any page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("new");
    if (t === "move_out" || t === "move_in" || t === "periodic") {
      setActiveType(t);
      setShowCreate(true);
      // Clean URL so refreshes don't re-pop
      window.history.replaceState({}, "", "/admin/inspections");
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jobs");
      const arr = Array.isArray(r.data) ? r.data : (r.data?.jobs || []);
      // Filter out `merged` status so merged duplicates disappear from the list.
      setJobs(arr.filter(j => j.inspection_type && j.status !== "merged"));
    } catch (e) {
      toast.error("Could not load inspections");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { move_out: 0, move_in: 0, periodic: 0 };
    jobs.forEach(j => { if (c[j.inspection_type] !== undefined) c[j.inspection_type]++; });
    return c;
  }, [jobs]);

  const filtered = useMemo(() => {
    const rows = jobs.filter(j => j.inspection_type === activeType);
    // Dedupe by physical unit — one address+unit yields one canonical row,
    // with a `_related_count` pill so users know how many raw WOs stack.
    // Jobs without an address fall back to their own id (never merged).
    const norm = (s) => (s || "").trim().toLowerCase();
    const byKey = new Map();
    for (const j of rows) {
      const addr = norm(j.property_address);
      const key = addr ? `${addr}::${norm(j.unit)}` : `__no_addr__::${j.id || j._id}`;
      const prior = byKey.get(key);
      if (!prior) { byKey.set(key, { ...j, _related_count: 0, _related_ids: [] }); continue; }
      // Keep the newer canonical (created_at desc); track the older as related.
      const priorTime = new Date(prior.created_at || 0).getTime();
      const curTime = new Date(j.created_at || 0).getTime();
      if (curTime > priorTime) {
        byKey.set(key, {
          ...j,
          _related_count: prior._related_count + 1,
          _related_ids: [prior.id || prior._id, ...(prior._related_ids || [])],
        });
      } else {
        prior._related_count += 1;
        prior._related_ids.push(j.id || j._id);
      }
    }
    return [...byKey.values()].sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [jobs, activeType]);

  return (
    <AdminShell>
      <div className="p-4 sm:p-6" data-testid="inspections-board">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight inline-flex items-center gap-2">
              <Broadcast weight="fill" size={32} className="text-[#00C4CC]"/> InspectRadar<sup className="text-xs align-super">™</sup>
            </h1>
            <p className="mt-1 text-sm text-slate-600 font-medium">
              Sweep every room, log every appliance. Move-Out → promote to Turn → Turn completes → auto-creates Move-In. Periodic walks stay standalone.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-inspection-btn"
            className="tactile-btn h-11 px-4 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
            <Plus weight="bold" size={16}/> New Sweep
          </button>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          {TYPES.map(t => (
            <button key={t.key} onClick={() => setActiveType(t.key)}
              data-testid={`insp-tab-${t.key}`}
              className={`h-12 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-2 ${
                activeType === t.key ? `${t.pillClass} text-white shadow-[3px_3px_0px_#0f172a]` : "bg-white hover:bg-slate-50"
              }`}>
              {t.label}
              <span className={`px-2 py-0.5 text-[10px] ${activeType === t.key ? "bg-white/30" : "bg-slate-200"}`}>
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500 font-medium">
          {TYPES.find(t => t.key === activeType)?.desc}
        </p>

        {loading ? (
          <div className="mt-8 text-center text-slate-500 font-bold">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 p-12 text-center border-2 border-dashed border-slate-300 text-slate-400" data-testid="insp-empty-state">
            <MagnifyingGlass weight="thin" size={48} className="mx-auto mb-2"/>
            <div className="font-black uppercase text-sm">No {TYPES.find(t => t.key === activeType)?.label} inspections yet</div>
            <div className="text-xs mt-1">Click "New Sweep" to start an InspectRadar™ session.</div>
          </div>
        ) : (
          <ul className="mt-6 space-y-2">
            {filtered.map(j => (
              <li key={j.id} data-testid={`insp-row-${j.id}`}
                className="block p-4 border-2 border-[#0f172a] bg-white hover:bg-slate-50 transition-colors">
                <Link to={`/admin/jobs/${j.id}?walk=1`} className="block">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={j.status || "assigned"}/>
                        {j.promoted_to_turn_id && (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-amber-500 text-white tracking-wider">→ Turn Created</span>
                        )}
                        {j.source_turn_id && (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-emerald-600 text-white tracking-wider">Auto from Turn</span>
                        )}
                        {j._related_count > 0 && (
                          <button
                            data-testid={`insp-row-merge-${j.id}`}
                            onClick={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              if (!window.confirm(
                                `Merge ${j._related_count} duplicate ${activeType.replace('_', '-')} entr${j._related_count === 1 ? 'y' : 'ies'} into this one?\n\n` +
                                `Photos, notes, and receipts from the duplicates will be copied here. ` +
                                `The other entries will be hidden (status → merged) but not deleted.`
                              )) return;
                              api.post("/jobs/merge-duplicates", {
                                keep_id: j.id,
                                merge_ids: j._related_ids || [],
                              }).then(r => {
                                toast.success(`Merged ${r.data.merged_count} duplicate${r.data.merged_count === 1 ? '' : 's'}`);
                                load();
                              }).catch(e => toast.error(formatApiErrorDetail(e?.response?.data?.detail || "Merge failed")));
                            }}
                            title={`Click to merge ${j._related_count} duplicate WO(s) into this one`}
                            className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-slate-800 hover:bg-emerald-600 text-white tracking-wider inline-flex items-center gap-1 transition-colors">
                            <StackSimple weight="fill" size={10}/> Merge +{j._related_count}
                          </button>
                        )}
                        <span className="font-black text-base truncate">{j.title || "—"}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><House size={12} weight="bold"/> {j.property_address}{j.unit ? ` · ${j.unit}` : ""}</span>
                        {j.assigned_to_name && <span>· {j.assigned_to_name}</span>}
                        <span className="text-slate-400">· {new Date(j.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ArrowRight weight="bold" size={18} className="text-slate-400"/>
                  </div>
                </Link>
                <ReportActions job={j}/>
                <ConvertTypeMenu job={j} onConverted={load}/>
              </li>
            ))}
          </ul>
        )}

        {showCreate && (
          <NewInspectionModal
            onClose={() => setShowCreate(false)}
            onCreated={async () => { setShowCreate(false); await load(); }}
          />
        )}
      </div>
    </AdminShell>
  );
}

function NewInspectionModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    inspection_type: "move_out",
    property_address: "",
    unit: "",
    customer_name: "",
    scheduled_for: "",
    bedrooms: 2,
    bathrooms: 1,
    half_baths: 0,
    has_kitchen: true,
    has_living: true,
    has_dining: false,
    has_hallway: false,
    has_stairs: false,
    has_basement: false,
    has_garage: false,
    has_yard: false,
    has_deck: false,
    has_porch: false,
    has_laundry: false,
    has_utility: false,
    has_office: false,
    has_foyer: false,
    has_attic: false,
    has_pantry: false,
    has_closet: false,
  });
  const [appliances, setAppliances] = useState(new Set(["Refrigerator", "Range / Stove", "Dishwasher"]));
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const toggleAppliance = (t) => setAppliances(prev => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.property_address.trim()) { toast.error("Property address required"); return; }
    setBusy(true);
    try {
      const typeLabel = TYPES.find(t => t.key === f.inspection_type)?.label || "Inspection";
      const roomCfg = { bedrooms: Number(f.bedrooms) || 0, bathrooms: Number(f.bathrooms) || 0, half_baths: Number(f.half_baths) || 0, custom_rooms: [] };
      AREA_OPTIONS.forEach(([k]) => { roomCfg[k] = !!f[k]; });
      const payload = {
        title: `${typeLabel} Sweep · ${f.property_address}`,
        description: `${typeLabel} InspectRadar™ sweep — walk & document every area + appliance.`,
        property_address: f.property_address.trim(),
        unit: f.unit.trim(),
        customer_name: f.customer_name.trim(),
        scheduled_for: f.scheduled_for || null,
        inspection_type: f.inspection_type,
        is_turn: false,
        room_config: roomCfg,
        seed_appliances: Array.from(appliances),
      };
      const r = await api.post("/jobs", payload);
      toast.success(`${typeLabel} sweep created`);
      // Land straight in the walk — no scrolling through job-detail first
      if (r.data?.id) nav(`/admin/jobs/${r.data.id}?walk=1`);
      await onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        data-testid="new-inspection-modal"
        className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-2xl inline-flex items-center gap-2">
            <Broadcast weight="fill" size={24} className="text-[#00C4CC]"/> New InspectRadar<sup className="text-[10px]">™</sup>
          </h3>
          <button type="button" onClick={onClose} data-testid="new-insp-close" className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>

        <div className="mt-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">Type</div>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map(t => (
              <button key={t.key} type="button" onClick={() => setF({ ...f, inspection_type: t.key })}
                data-testid={`new-insp-type-${t.key}`}
                className={`h-16 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider transition-all ${
                  f.inspection_type === t.key ? `${t.pillClass} text-white shadow-[3px_3px_0px_#0f172a]` : "bg-white hover:bg-slate-50"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">{TYPES.find(t => t.key === f.inspection_type)?.desc}</p>
        </div>

        <label className="mt-4 block">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Property Address *</span>
          <input value={f.property_address} onChange={e => setF({ ...f, property_address: e.target.value })}
            data-testid="new-insp-address" placeholder="1346 S 30th St"
            className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
        </label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Unit</span>
            <input value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })}
              data-testid="new-insp-unit" placeholder="A · 101"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Scheduled</span>
            <input type="datetime-local" value={f.scheduled_for} onChange={e => setF({ ...f, scheduled_for: e.target.value })}
              data-testid="new-insp-scheduled"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
        </div>
        <label className="mt-3 block">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Tenant Name (optional)</span>
          <input value={f.customer_name} onChange={e => setF({ ...f, customer_name: e.target.value })}
            data-testid="new-insp-tenant"
            className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
        </label>

        <div className="mt-4 p-3 border-2 border-slate-300 bg-slate-50">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2 inline-flex items-center gap-1">
            <Broadcast weight="fill" size={12} className="text-[#00C4CC]"/> Unit Layout · Radar Sweep Areas
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Bedrooms</span>
              <input type="number" min="0" max="10" value={f.bedrooms}
                onChange={e => setF({ ...f, bedrooms: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="new-insp-bedrooms"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Full Baths</span>
              <input type="number" min="0" max="10" value={f.bathrooms}
                onChange={e => setF({ ...f, bathrooms: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="new-insp-bathrooms"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500" title="Toilet + vanity, no shower">Half Baths</span>
              <input type="number" min="0" max="10" value={f.half_baths}
                onChange={e => setF({ ...f, half_baths: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="new-insp-half-baths"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
            </label>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
            {AREA_OPTIONS.map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!f[k]}
                  onChange={e => {
                    const on = e.target.checked;
                    setF({ ...f, [k]: on });
                    if (on && AREA_TO_APPLIANCES[k]) {
                      setAppliances(prev => {
                        const next = new Set(prev);
                        AREA_TO_APPLIANCES[k].forEach(a => next.add(a));
                        return next;
                      });
                    }
                  }}
                  data-testid={`new-insp-${k}`} className="h-4 w-4 accent-[#0f172a]"/>
                <span className="font-medium text-xs">{lbl}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 p-3 border-2 border-[#00C4CC] bg-cyan-50">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-800 inline-flex items-center gap-1">
              <Wrench weight="fill" size={12} className="text-[#00C4CC]"/> Appliances Present ({appliances.size})
            </div>
            <div className="text-[10px] text-slate-500 font-medium">Seeded — scan data-plates in the field</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-sm">
            {APPLIANCE_SEED_TYPES.map(t => {
              const on = appliances.has(t);
              return (
                <button key={t} type="button" onClick={() => toggleAppliance(t)}
                  data-testid={`new-insp-appliance-${t.replace(/\W+/g, "-").toLowerCase()}`}
                  className={`h-9 px-2 border-2 font-black uppercase text-[10px] tracking-wider transition-all ${
                    on ? "border-[#00C4CC] bg-[#00C4CC] text-white shadow-[2px_2px_0px_#0f172a]"
                       : "border-[#0f172a] bg-white text-[#0f172a] hover:bg-cyan-50"
                  }`}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} data-testid="new-insp-cancel"
            className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="new-insp-submit"
            className="tactile-btn flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-60 inline-flex items-center justify-center gap-1">
            <Broadcast weight="fill" size={14}/> {busy ? "Sweeping…" : "Start Sweep"}
          </button>
        </div>
      </form>
    </div>
  );
}
